/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSVLink } from 'react-csv';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
    Container,
    ButtonsContainer,
    StyledCSVLink,
    StyledChipSelect,
    StyledAccordion,
    StyledLabel,
    AccordionBody,
} from './styled';

import { Button, ButtonVariant, ButtonSize } from '../../../Button';
import { Checkbox } from '../../../Checkbox';
import { Divider } from '../../../Divider';
import { FormInput } from '../../../FormInput';
import { StatusSelectMenu } from '../../../StatusSelectMenu';
import { useExportContext } from '../../context/ExportContext';
import { SuccessModal } from '../SuccessModal';

interface IExportModalProps {
    rowsToBeExported: () => Promise<any[]>;
    isOpen: boolean;
    onClose: () => void;
    exportFileName?: string;
    title: string;
}

const FILENAME_INPUT = 'FileName';
const FIELD_FILTER_INPUT = 'excludedFields';
const FIELD_MARK_EDMS_AS = 'markEdmsAs';

const getDefaultName = () =>
    `${new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
    })}_ActiveLeads_CatalyzeAI.csv`;

const exportSchema = yup.object({
    [FIELD_FILTER_INPUT]: yup.array().of(yup.string()).required(),
    [FIELD_MARK_EDMS_AS]: yup.string(),
    [FILENAME_INPUT]: yup.string().required(`File name can't be blank.`),
});

export function ExportModal<TExportData extends { [key: string]: unknown }>({
    rowsToBeExported,
    isOpen,
    onClose,
    exportFileName,
    title,
}: IExportModalProps) {
    const csvLinkRef = useRef<CSVLink>(null);
    const clickedRef = useRef(false);
    const [csvData, setCsvData] = useState<Array<Partial<TExportData>>>([]);
    const [isSuccess, setSuccess] = useState(false);
    const formMethods = useForm({
        resolver: yupResolver(exportSchema),
        mode: 'onTouched',
        defaultValues: {
            excludedFields: [] as string[],
            markEdmsAs: '',
            FileName: exportFileName ?? getDefaultName(),
        },
    });
    const exportContext = useExportContext<TExportData>();
    const {
        filterData,
        excludeOptions: chipSelectOptions,
        markAsOptions,
        onExport,
    } = exportContext?.exportState ?? {};

    const { handleSubmit, watch, setValue, reset } = formMethods;
    const [excludedFields, markAs, inputName] = watch([
        FIELD_FILTER_INPUT,
        FIELD_MARK_EDMS_AS,
        FILENAME_INPUT,
    ]);

    useEffect(() => {
        if (isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const onSubmitHandler = useCallback(async () => {
        clickedRef.current = true;
        const data = (await rowsToBeExported()) ?? [];
        const filteredData = filterData
            ? filterData?.(data, excludedFields) ?? data
            : data;
        onExport?.({
            markAs: markAsOptions?.find((opt) => opt.value === markAs),
            rows: data,
        });
        setCsvData(filteredData);
    }, [
        excludedFields,
        rowsToBeExported,
        filterData,
        onExport,
        markAs,
        markAsOptions,
    ]);

    useEffect(() => {
        if (clickedRef.current) {
            clickedRef.current = false;
            // @ts-ignore link is not typed, but this way we simulate the click on it.
            csvLinkRef.current?.link?.click();
            setSuccess(true);
        }
    }, [csvData, clickedRef, csvLinkRef]);

    const onCloseHandler = useCallback(() => {
        setSuccess(false);
        onClose();
    }, [onClose]);

    const defaultMarkAsOption = markAsOptions?.[0];

    const onCheckboxChange = useCallback(
        (checked: boolean) => {
            if (checked) {
                setValue(FIELD_MARK_EDMS_AS, defaultMarkAsOption?.value ?? '');
            } else {
                setValue(FIELD_MARK_EDMS_AS, ''); // for some reason resetField doesn't work
            }
        },
        [defaultMarkAsOption, setValue]
    );

    const shouldShowChipSelect = (chipSelectOptions?.length ?? 0) > 0;
    const shouldShowMarkAs = false; // (markAsOptions?.length ?? 0) > 0;

    return (
        <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitHandler)}>
                <SuccessModal
                    isOpen={isOpen}
                    onClose={onCloseHandler}
                    isSuccess={isSuccess}
                    successTitle="Exported Successfully"
                    successText="Check out your downloads folder"
                    title={title}
                    footer={
                        <ButtonsContainer>
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                type="button"
                                onClick={onCloseHandler}
                                size={ButtonSize.SMALL}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant={ButtonVariant.PRIMARY}
                                type="submit"
                                size={ButtonSize.SMALL}
                            >
                                Export
                            </Button>
                            <StyledCSVLink
                                ref={csvLinkRef}
                                target="_blank"
                                data={csvData}
                                filename={inputName}
                            />
                        </ButtonsContainer>
                    }
                >
                    <Container>
                        {shouldShowChipSelect && (
                            <>
                                <StyledChipSelect
                                    inputName={FIELD_FILTER_INPUT}
                                    title="Include"
                                    options={chipSelectOptions}
                                />
                                <Divider />
                            </>
                        )}
                        {shouldShowMarkAs && (
                            <>
                                <StyledAccordion
                                    title="Additional options"
                                    isInitiallyOpen
                                >
                                    <AccordionBody>
                                        <Checkbox
                                            uniqueKey={`${FIELD_MARK_EDMS_AS}Checkbox`}
                                            handleOnChange={onCheckboxChange}
                                            checked={Boolean(markAs)}
                                        />
                                        <StyledLabel
                                            htmlFor={`${FIELD_MARK_EDMS_AS}Checkbox`}
                                        >
                                            Mark EDMs as
                                        </StyledLabel>
                                        <StatusSelectMenu
                                            options={markAsOptions ?? []}
                                            onChange={(val) =>
                                                setValue(
                                                    FIELD_MARK_EDMS_AS,
                                                    val.value
                                                )
                                            }
                                            value={
                                                markAsOptions?.find(
                                                    (val) =>
                                                        val.value === markAs
                                                ) ?? markAsOptions![0]
                                            }
                                        />
                                    </AccordionBody>
                                </StyledAccordion>
                                <Divider />
                            </>
                        )}
                        <FormInput
                            id="FileFormat"
                            disabled
                            disabledValue="CSV (.csv)"
                            label="File Format"
                        />
                        <FormInput id={FILENAME_INPUT} label="Save As" />
                    </Container>
                </SuccessModal>
            </form>
        </FormProvider>
    );
}

ExportModal.defaultProps = {
    exportFileName: undefined,
};
